import { useCallback, useEffect, useMemo } from 'react'
import useAsyncEffect from 'use-async-effect'
import { useIntercom } from 'react-use-intercom'
import * as FullStory from '@fullstory/browser'
import { useSplitClient, useSplitTreatments } from '@splitsoftware/splitio-react'
import { useLocation } from 'react-router-dom'
import { isProduction, isAmplifyDevEnv } from 'utils/envs'
import { CHURN_ZERO_EVENTS as cz, ACCOUNT_PAYMENT_TYPE_ENUMS } from 'utils/constants'
import { setContact, trackEvent, stopCZTracking } from 'utils/tracking'
import { transformStrToCapital } from 'utils/router'

import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import './global.css'
import 'auth/cognito.css'
import { Hub, Auth } from 'aws-amplify'
import { clearAuthToken, setCurrentAuthToken, setUser, setOrgAndYardsDetails } from 'state/auth'
import { selectCamera, selectCameras, selectScales, updateDeviceList, selectScaleCameras } from 'state/devices'
import { useGetDevicesQuery } from 'services/devices'
import { cameraSelectorName, cashDrawerSelectorName } from 'components/containers/devices/EquipmentSelector'
import { useGetPermissionsQuery } from 'services/permissions'
import { useGetYardQuery } from 'services/yards'
import { setPermissions } from 'state/permissions'
import {
  setSettings,
  setYardDetails,
  setCashDrawers,
  selectCashDrawer,
  setDefaultCurrency,
  setDefaultYardUm,
  setHasAtm,
  setYardSettingsLoaded,
} from 'state/settings'
import { setErpType } from 'state/ledger'
import { setFeatureFlags } from 'state/featureFlags'
import { setCriticalDataLoading, setCriticalDataLoadingError } from 'state/loading'
import { useGetOrganizationQuery } from 'services/organizations'
import { useGetAccountsQuery } from 'services/accounts'
import { useGetSettingsQuery } from 'services/settings'
import { useGetLockAccountingPeriodQuery } from 'services/lockAccounting'
import { featureFlagsDict } from 'utils/featureFlagsDict'
import { useAppDispatch, useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { useRefetchAfterError } from 'utils/custom-hooks/useRefetchAfterError'
import { ORG_MODE_STATUS_LOCAL_STORAGE_KEY, setOrgMode } from './state/ui'

const AppSetup = () => {
  const { update } = useIntercom()
  const { client: splitClient, isReady: splitReady } = useSplitClient()
  const dispatch = useAppDispatch()
  const { pathname } = useLocation()

  const { treatments } = useSplitTreatments({
    names: Object.keys(featureFlagsDict),
  })

  const { user, token, yardId, selectedYard } = useAppSelector(state => state.auth)

  const { data: accounts, error: accountsError, refetch: refetchAccounts } = useGetAccountsQuery({}, { skip: !token })
  const {
    data: settings,
    error: settingsError,
    refetch: refetchSettings,
  } = useGetSettingsQuery({ origin: 'App' }, { skip: !yardId })
  const {
    data: devices,
    error: devicesError,
    refetch: refetchDevices,
  } = useGetDevicesQuery({}, { skip: !token || !user })
  const {
    data: permissionsData,
    error: permissionsError,
    refetch: refetchPermissions,
  } = useGetPermissionsQuery({}, { skip: !token })
  const {
    data: yardData,
    error: yardError,
    refetch: refetchYard,
  } = useGetYardQuery({ yardId: yardId || '' }, { skip: !yardId })
  const {
    data: organizationData,
    error: organizationError,
    refetch: refetchOrganization,
  } = useGetOrganizationQuery({}, { skip: !token })
  const {
    data: lockAccountingPeriodData,
    error: lockAccountingPeriodError,
    refetch: refetchLockAccountingPeriod,
  } = useGetLockAccountingPeriodQuery({}, { skip: !token })

  // manually refetch the following key endpoints if they fail during app load
  const accountsFailure = useRefetchAfterError(refetchAccounts, accountsError)
  const settingsFailure = useRefetchAfterError(refetchSettings, settingsError)
  const devicesFailure = useRefetchAfterError(refetchDevices, devicesError)
  const permissionsFailure = useRefetchAfterError(refetchPermissions, permissionsError)
  const yardFailure = useRefetchAfterError(refetchYard, yardError)
  const organizationFailure = useRefetchAfterError(refetchOrganization, organizationError)
  const lockAccountingPeriodFailure = useRefetchAfterError(refetchLockAccountingPeriod, lockAccountingPeriodError)

  const criticalError =
    accountsFailure ||
    settingsFailure ||
    devicesFailure ||
    permissionsFailure ||
    yardFailure ||
    organizationFailure ||
    lockAccountingPeriodFailure

  useEffect(() => {
    if (!token) {
      setCriticalDataLoading(false)
      return
    }
    const missingCriticalData =
      !accounts ||
      !settings ||
      !devices ||
      !permissionsData ||
      !yardData ||
      !organizationData ||
      !lockAccountingPeriodData
    dispatch(setCriticalDataLoading(missingCriticalData))
  }, [token, accounts, settings, devices, permissionsData, yardData, organizationData, lockAccountingPeriodData])

  useEffect(() => {
    if (criticalError) {
      // Log all components of the criticalError flag for debugging purposes
      console.error('Critical error during app setup:', {
        accountsError,
        settingsError,
        devicesError,
        permissionsError,
        yardError,
        organizationError,
        lockAccountingPeriodError,
      })

      dispatch(setCriticalDataLoadingError(true))
      dispatch(setCriticalDataLoading(false))
    }
  }, [criticalError])

  useEffect(() => {
    // prevent mousewheel actions from changing values in number inputs
    const wheelCallback = () => {
      const documentActiveElement = document?.activeElement as HTMLInputElement
      if (documentActiveElement?.type === 'number') {
        documentActiveElement?.blur()
      }
    }
    document.addEventListener('wheel', wheelCallback)
    return () => {
      document.removeEventListener('wheel', wheelCallback)
    }
  }, [])

  useEffect(() => {
    const segments = pathname.split('/')
    const lastSegment = segments[segments.length - 1] || ''
    const secondLastSegment = segments[segments.length - 2] || ''

    const pageSegment = /\d/.test(lastSegment) || !lastSegment ? secondLastSegment : lastSegment
    document.title = transformStrToCapital(pageSegment)
  }, [pathname])

  useEffect(() => {
    if (accounts?.Items && yardData) {
      dispatch(
        setCashDrawers(
          accounts?.Items.filter(item => {
            if (item.type === ACCOUNT_PAYMENT_TYPE_ENUMS.CASH) {
              return true
            }
            if (item.type === ACCOUNT_PAYMENT_TYPE_ENUMS.ATM && yardData?.Items[0].hasAtm) {
              return true
            }
            return false
          }).sort((a, b) => {
            if (a.accountName === 'Default Cash Drawer') return -1
            if (b.accountName === 'Default Cash Drawer') return 1
            if (a.accountName < b.accountName) return -1
            if (a.accountName > b.accountName) return 1
            return 0
          }),
        ),
      )
    }
  }, [accounts, yardData])

  /**
   * We set yard settings loaded independently of the settings object
   * This should only happen on hard refresh or app reload and allows app to distinguish between default settings
   * and settings that are loaded from the server
   */
  useEffect(() => {
    if (typeof settings === 'object') {
      dispatch(setSettings(settings as any))
      dispatch(setYardSettingsLoaded(true)) // these settings are from BE
    }
  }, [settings])

  useEffect(() => {
    if (typeof lockAccountingPeriodData === 'object') {
      dispatch(
        setSettings({ overviewSettings: { overview: { lockAccountingPeriod: { ...lockAccountingPeriodData } } } }),
      )
    }
  }, [lockAccountingPeriodData])

  useEffect(() => {
    if (settings?.erpSettings?.general?.erpType) {
      dispatch(setErpType(settings.erpSettings.general.erpType))
    }
  }, [settings?.erpSettings?.general?.erpType])

  useEffect(() => {
    if (devices) {
      dispatch(updateDeviceList(devices))
    }
  }, [devices])

  useEffect(() => {
    if (permissionsData?.roles && permissionsData.roles.length > 0) {
      dispatch(setPermissions(permissionsData))
    }
  }, [permissionsData])

  useEffect(() => {
    if (organizationData && organizationData.id && user) {
      setContact(organizationData.id, user?.attributes?.email)
    }
  }, [organizationData, user])

  useEffect(() => {
    if (user?.attributes?.sub && selectedYard) {
      dispatch(
        setOrgMode({
          newValue: window.localStorage.getItem(ORG_MODE_STATUS_LOCAL_STORAGE_KEY + user.attributes.sub) === 'true',
          sub: user.attributes.sub,
        }),
      )

      const selectedCameraId = localStorage.getItem(`${user.attributes.sub}:${cameraSelectorName}`)
      const selectedScaleIds = JSON.parse(localStorage.getItem(`${user.attributes.sub}:scales`) || '[]')
      const selectedCashDrawerId = localStorage.getItem(
        `${user.attributes.sub}:${cashDrawerSelectorName}-${selectedYard}`,
      )
      const selectedCameraIds = JSON.parse(localStorage.getItem(`${user.attributes.sub}:cameras`) || '[]')
      const selectedScaleCameras = JSON.parse(localStorage.getItem(`${user.attributes.sub}:scaleCameras`) || '{}')
      dispatch(selectCamera(selectedCameraId || 'native'))
      dispatch(selectScales(selectedScaleIds))

      if (selectedCashDrawerId) dispatch(selectCashDrawer(selectedCashDrawerId))
      dispatch(selectCameras(selectedCameraIds))
      dispatch(selectScaleCameras(selectedScaleCameras))
    }
  }, [user?.attributes, selectedYard])

  useEffect(() => {
    if (organizationData && organizationData.id) {
      dispatch(
        setOrgAndYardsDetails({
          organizationDetails: organizationData,
          yardDetailsList: organizationData.yards,
        }),
      )
    }
  }, [organizationData])

  useEffect(() => {
    if (organizationData?.id && user?.attributes?.sub && selectedYard && yardId && splitReady) {
      splitClient?.setAttributes({
        organizationId: organizationData?.id,
        organization: organizationData?.name,
        userId: user?.attributes?.sub,
        yardId,
        yard: selectedYard,
      })
    }
  }, [user?.attributes, organizationData, selectedYard, yardId, splitClient, splitReady])

  useEffect(() => {
    if (yardData?.Items?.[0] && organizationData) {
      const { logo = '' } = organizationData
      const {
        addressName,
        address,
        address2,
        city,
        state,
        zip,
        country,
        defaultCurrency,
        defaultUOM,
        hasAtm,
        phoneNumber,
        locationId,
        defaultLocationId,
        gsPayEnabled,
        // eslint-disable-next-line no-unsafe-optional-chaining
      } = yardData?.Items?.[0]
      dispatch(
        setYardDetails({
          addressName,
          address,
          address2,
          city,
          state,
          zip,
          country,
          logo,
          phoneNumber,
          locationId,
          defaultLocationId,
          gsPayEnabled,
        }),
      )
      if (defaultCurrency) {
        // if this is undefined, we will use the value in the initial state: "USD"
        dispatch(setDefaultCurrency(defaultCurrency))
      }
      if (defaultUOM) {
        // if this is undefined, we will use the value in the initial state: "LBS"
        dispatch(setDefaultYardUm(defaultUOM))
      }
      dispatch(setHasAtm(hasAtm))
    }
  }, [yardData, organizationData])

  useEffect(() => {
    const company = organizationData && organizationData.id ? { company_id: organizationData.id } : null
    if (user && pathname) {
      const email = user?.attributes?.email
      const orgName = organizationData?.name
      const disableIntercomMessaging = orgName === 'test-org'

      if (isAmplifyDevEnv || isProduction) {
        update({ email, pathname, company, hideDefaultLauncher: disableIntercomMessaging })
      }

      if (process.env.REACT_APP_IS_AMPLIFY_ENV && isProduction) {
        FullStory.identify(email)
      }
    }
  }, [pathname, user, organizationData])

  useEffect(() => {
    if (Object.keys(treatments).length > 0) {
      dispatch(setFeatureFlags(treatments))
    }
  }, [treatments])

  const handleCognitoUser = useCallback(
    async isActive => {
      if (!user?.attributes) {
        try {
          const cognitoUser = await Auth.currentAuthenticatedUser()

          if (!isActive()) {
            return
          }

          dispatch(setUser({ userAttributes: cognitoUser?.attributes }))
        } catch (err) {
          console.error(err)
        }
      }
    },
    [user?.attributes],
  )

  useAsyncEffect(
    async isActive => {
      await handleCognitoUser(isActive)
    },
    [user?.attributes],
  )

  Hub.listen('auth', async event => {
    switch (event?.payload?.event) {
      case 'signIn':
        trackEvent(cz.LOG_IN)
        dispatch(setCurrentAuthToken())

        await handleCognitoUser(() => true)
        break
      case 'signOut':
        stopCZTracking()
        dispatch(clearAuthToken())
        break
      default:
    }
  })

  return null
}

export default AppSetup
